import { createContext, useContext, useEffect, useState } from "react";
import { loginUser, logoutUser } from "../services/authservice";
import { getProfile } from "../services/profileservice";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  /* Load user on app start */
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.clear();
      }
    }

    // Refresh profile from backend if token exists
    const token = localStorage.getItem("accessToken");
    if (token) {
      getProfile()
        .then((res) => {
          const profileData = res.data || res;
          const updatedUser = { ...JSON.parse(localStorage.getItem("user") || "{}"), ...profileData };
          localStorage.setItem("user", JSON.stringify(updatedUser));
          setUser(updatedUser);
        })
        .catch(() => {
          // Token expired or invalid — clear and redirect
          localStorage.clear();
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  /* Apply Theme to DOM */
  useEffect(() => {
    if (user && user.theme) {
      if (user.theme === "system") {
        const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
      } else {
        document.documentElement.setAttribute("data-theme", user.theme);
      }
    } else {
      document.documentElement.setAttribute("data-theme", "light"); // Default if no preference
    }
  }, [user]);

  /* LOGIN */
  const login = async (credentials) => {
    const userData = await loginUser(credentials);
    setUser(userData);
    return userData;
  };

  /* LOGOUT */
  const logout = async () => {
    await logoutUser();
    setUser(null);
  };

  /* REFRESH CURRENT USER & PERMISSIONS */
  const refreshUser = async () => {
    try {
      const res = await getProfile();
      const updatedUser = res.data || res;
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (err) {
      console.error("Failed to refresh user permissions:", err);
    }
  };

  /* UPDATE USER */
  const updateUser = (newData) => {
    const updated = { ...user, ...newData };
    localStorage.setItem("user", JSON.stringify(updated));
    setUser(updated);
  };

  /* RBAC HELPERS */
  const hasRole = (roleName) => {
    if (!user || !roleName) return false;
    const reqRole = String(roleName).toLowerCase();
    
    // Treat Super Admin as an admin
    if (reqRole === "admin" && (
      (user.role && String(user.role).toLowerCase() === "super admin") || 
      (Array.isArray(user.roles) && user.roles.some(r => String(r).toLowerCase() === "super admin"))
    )) {
      return true;
    }

    if ((user.is_superuser || user.is_staff) && reqRole === "admin") return true;
    if (user.role && String(user.role).toLowerCase() === reqRole) return true;
    if (Array.isArray(user.roles) && user.roles.some(r => String(r).toLowerCase() === reqRole)) return true;
    return false;
  };

  const hasPermission = (codename) => {
    if (!user || !codename) return false;
    if (user.is_superuser || hasRole("super admin")) return true;
    if (!Array.isArray(user.permissions)) return false;
    return user.permissions.includes(codename);
  };

  const hasAnyPermission = (codenamesArray) => {
    if (!user || !Array.isArray(codenamesArray)) return false;
    if (user.is_superuser || hasRole("super admin")) return true;
    return codenamesArray.some((code) => hasPermission(code));
  };

  const hasAllPermissions = (codenamesArray) => {
    if (!user || !Array.isArray(codenamesArray)) return false;
    if (user.is_superuser || hasRole("super admin")) return true;
    return codenamesArray.every((code) => hasPermission(code));
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, updateUser, refreshUser, isAuthenticated: !!user,
      hasRole, hasPermission, hasAnyPermission, hasAllPermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
